import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, DropdownList } from '.';
import { toFilterString } from '../../../util/string.util';
import Icon, { IconType } from '../../icons/Icon';
import { Feedback } from '../Feedback/feedback';
import TextInput from '../TextInput/TextInput';
import { v4 } from 'uuid';
import useControllableState, { StateManager } from '../../../hooks/useControllableState';

interface DropdownSearchSelectBaseProps<T> {
  label?: string;
  placeholder: string;

  items: T[];
  textBuilder: (item: T) => string;
  itemBuilder?: (item: T) => React.ReactNode;
  searchBuilder?: (item: T) => string;

  mandatory?: boolean;
  disabled?: boolean;
  feedback?: (item: T | undefined) => Feedback.Props | undefined;

  first?: React.ReactNode;
  lastBuilder?: (options: { input: string; selected: boolean }) => React.ReactNode;

  leading?: React.ReactNode;
  tooltip?: string | React.ReactNode;

  openManager?: StateManager<boolean>;
}

interface DropdownSearchSelectProps<T> extends DropdownSearchSelectBaseProps<T> {
  /** The currently selected value */
  value: T | undefined;
  onSelect: (item: T | undefined) => void;
}

export default function DropdownSearchSelect<T>({
  value,
  onSelect,
  openManager,
  label,
  placeholder,
  items,
  textBuilder,
  itemBuilder,
  searchBuilder,
  lastBuilder,
  mandatory = false,
  disabled = false,
  feedback = mandatory ? item => Feedback.mandatory<T>(item) : item => undefined,
  leading,
  tooltip,
}: DropdownSearchSelectProps<T>) {
  const inputRef = useRef<HTMLInputElement>(null);

  const [opened, setOpened] = useState(true);
  const [feedbackProps, setFeedbackProps] = useState<Feedback.Props | undefined>(undefined);
  const [open, setOpen] = useControllableState(false, openManager);

  const [search, setSearch] = useState('');

  useEffect(() => {
    if (open && !opened) setOpened(true);
  }, [open]);

  useEffect(() => {
    if (!opened) setFeedbackProps(feedback(value));
  }, [value, mandatory]);

  const handleSelect = (item: T | undefined) => {
    onSelect(item);
    setSearch('');
    setOpen(false);
  };

  const Trailing = () => {
    if (disabled) return null;
    if (!value) return <Icon type={IconType.chevronDown} size={'h-5 w-5'} />;

    return (
      <Icon
        type={IconType.xClose}
        className='cursor-pointer'
        size={'h-5 w-5'}
        onClick={event => {
          handleSelect(undefined);
          if (inputRef.current) setTimeout(() => inputRef.current?.focus(), 0);
        }}
      />
    );
  };

  const shownItems = useMemo(() => {
    return items.filter(i =>
      toFilterString(searchBuilder ? searchBuilder(i) : textBuilder(i)).includes(toFilterString(search)),
    );
  }, [items, search]);

  const [externalUpdate, setExternalUpdate] = useState(v4());
  useEffect(() => {
    setExternalUpdate(v4());
  }, [feedbackProps]);

  return (
    <div className='grid gap-1'>
      <Dropdown
        externalUpdate={externalUpdate}
        open={open}
        setOpen={setOpen}
        dropdown={
          <DropdownList
            items={shownItems}
            textBuilder={textBuilder}
            itemBuilder={itemBuilder}
            onClick={handleSelect}
            lastBuilder={lastBuilder ? selected => lastBuilder!({ input: search, selected }) : undefined}
          />
        }
      >
        <TextInput
          leading={leading}
          ref={inputRef}
          disabled={disabled || !!value}
          value={value ? textBuilder(value) : search}
          onChange={search => setSearch(search)}
          trailing={<Trailing />}
          label={label}
          mandatory={mandatory}
          feedback={null}
          placeholder={placeholder}
          tooltip={tooltip}
          onFocus={() => setOpen(true)}
          type='search'
          autoComplete={`section one-time-code`}
        />
      </Dropdown>
      {feedbackProps?.text && !open && (
        <span className='flex gap-1'>
          {Feedback.icons[feedbackProps.type]}
          <p className={`text-xs ${Feedback.textColors[feedbackProps.type]}`}>{feedbackProps.text}</p>
        </span>
      )}
    </div>
  );
}

type IdentifierBuilder<T, I = string> = keyof T | ((item: T) => I);
function getIdentifier<T, I = string>(item: T, identifierBuilder: IdentifierBuilder<T, I>): I {
  if (typeof identifierBuilder === 'function') {
    return identifierBuilder(item);
  }
  return item[identifierBuilder] as I;
}

interface DropdownSearchSelectByIdProps<T, I = string> extends DropdownSearchSelectBaseProps<T> {
  identifierBuilder?: IdentifierBuilder<T, I>;
  value: I | undefined;
  onSelect: (id: I | undefined) => void;
}

export function DropdownSearchSelectById<T, I = string>(props: DropdownSearchSelectByIdProps<T, I>) {
  const identifierBuilder = props.identifierBuilder || ('id' as keyof T);
  const value = props.value && props.items.find(item => getIdentifier(item, identifierBuilder) === props.value);
  const onSelect = (item: T | undefined) => props.onSelect(item && getIdentifier(item, identifierBuilder));

  return <DropdownSearchSelect {...props} value={value} onSelect={onSelect} />;
}
